import { useEffect, useState } from 'react';

export interface LaunchCountdown {
  hours: string;
  minutes: string;
  seconds: string;
}

const COUNTDOWN_MINUTES = 10;
const STORAGE_KEY = 'crochet-launch-deadline';

let cachedDeadline: number | null = null;

/**
 * The launch window lasts 10 minutes per visitor and is stored locally, so
 * refreshing or navigating back does not restart it. Read once per page load.
 */
function getDeadline(): number {
  if (cachedDeadline !== null) return cachedDeadline;
  const fallback = Date.now() + COUNTDOWN_MINUTES * 60 * 1000;
  try {
    const parsed = Number(window.localStorage.getItem(STORAGE_KEY) ?? Number.NaN);
    if (Number.isFinite(parsed)) {
      cachedDeadline = parsed;
    } else {
      window.localStorage.setItem(STORAGE_KEY, String(fallback));
      cachedDeadline = fallback;
    }
  } catch {
    cachedDeadline = fallback;
  }
  return cachedDeadline;
}

function getRemaining(): number {
  return Math.max(0, getDeadline() - Date.now());
}

function pad(value: number): string {
  return value.toString().padStart(2, '0');
}

/**
 * Only flips once, when the window closes. Components that just need to know
 * whether the offer expired never re-render while the clock ticks.
 */
export function useLaunchExpired(): boolean {
  const [isExpired, setIsExpired] = useState(() => getRemaining() <= 0);

  useEffect(() => {
    if (isExpired) return undefined;
    const timer = window.setTimeout(() => setIsExpired(true), getRemaining());
    return () => window.clearTimeout(timer);
  }, [isExpired]);

  return isExpired;
}

/**
 * Per-second ticker. Use it only inside the small component that displays the
 * digits. It pauses while the tab is hidden and re-syncs from the clock.
 */
export function useLaunchCountdown(): LaunchCountdown {
  const [remaining, setRemaining] = useState(getRemaining);

  useEffect(() => {
    let timer: number | undefined;

    const tick = () => {
      window.clearTimeout(timer);
      const next = getRemaining();
      setRemaining(next);
      if (next > 0 && !document.hidden) {
        timer = window.setTimeout(tick, 1000 - (Date.now() % 1000));
      }
    };

    tick();
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, []);

  const totalSeconds = Math.floor(remaining / 1000);
  return {
    hours: pad(Math.floor(totalSeconds / 3600)),
    minutes: pad(Math.floor((totalSeconds % 3600) / 60)),
    seconds: pad(totalSeconds % 60),
  };
}
